<template>
  <form
    novalidate
    class="flex h-full flex-col justify-between gap-12"
    @submit.prevent="onSubmit"
  >
    <JRow spaceMd="12" spaceLg="12" spaceXl="12" class="mb-12 gap-y-16">
      <JCol>
        <JTextInput
          v-model="firstName.value.value"
          :error="firstName.errorMessage.value"
          type="text"
          :label="$t('profile.firstName.label')"
          required
          :placeholder="$t('profile.firstName.placeholder')"
        />
      </JCol>
      <JCol>
        <JTextInput
          v-model="lastName.value.value"
          :error="lastName.errorMessage.value"
          type="text"
          :label="$t('profile.lastName.label')"
          required
          :placeholder="$t('profile.lastName.placeholder')"
        />
      </JCol>
      <JCol>
        <JTextInput
          v-model="email.value.value"
          :error="email.errorMessage.value"
          type="email"
          required
          :label="$t('profile.email.label')"
          :placeholder="$t('profile.email.placeholder')"
        />
      </JCol>
      <JCol>
        <JTelInput
          v-model="phone.value.value"
          :error="phone.errorMessage.value"
          :label="$t('affiliate.form.phoneNumber.label')"
          required
          :placeholder="$t('affiliate.form.phoneNumber.placeholder')"
          name="telephone"
          teleport
        />
      </JCol>
      <JCol>
        <JTextInput
          v-model="companyName.value.value"
          :error="companyName.errorMessage.value"
          type="text"
          :label="$t('affiliate.form.companyName.label')"
          :placeholder="$t('affiliate.form.companyName.placeholder')"
        />
      </JCol>
    </JRow>

    <div class="flex gap-12 pb-12">
      <JButton
        block
        color="secondary"
        variant="tonal"
        type="button"
        :disabled="isLoading"
        @click.prevent="emit('cancel')"
      >
        {{ $t('words.cancel') }}
      </JButton>

      <JButton block type="submit" :isLoading="isLoading" :disabled="isLoading">
        {{ $t('words.submit') }}
      </JButton>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { JButton, JCol, JRow, JTelInput, JTextInput } from '@journey/ui-kit'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import type { Emits, Form, Props } from './AffiliateFormFields.types'
import {
  MAXIMUM_NAME_LENGTH,
  MINIMUM_NAME_LENGTH,
} from '@/modules/Book/constants'
import { REGEX } from '@/modules/Core/constants'
import { yupSchema } from '@/modules/Core/utils'

defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const validationSchema = object({
  firstName: string()
    .matches(REGEX.ONLY_LETTERS, t('profile.firstName.validation.lettersOnly'))
    .min(
      MINIMUM_NAME_LENGTH,
      t('validation.firstName.minLength', { n: MINIMUM_NAME_LENGTH })
    )
    .max(
      MAXIMUM_NAME_LENGTH,
      t('validation.firstName.maxLength', { n: MAXIMUM_NAME_LENGTH })
    )
    .required(t('profile.firstName.validation.required')),
  lastName: string()
    .matches(REGEX.ONLY_LETTERS, t('profile.lastName.validation.lettersOnly'))
    .min(
      MINIMUM_NAME_LENGTH,
      t('validation.lastName.minLength', { n: MINIMUM_NAME_LENGTH })
    )
    .max(
      MAXIMUM_NAME_LENGTH,
      t('validation.lastName.maxLength', { n: MAXIMUM_NAME_LENGTH })
    )
    .required(t('profile.lastName.validation.required')),
  email: string()
    .matches(REGEX.EMAIL, t('profile.email.validation.valid'))
    .required(t('book.contactEmail.validation.required')),
  phone: yupSchema.phoneSchema(
    true,
    t('affiliate.form.phoneNumber.validation.required'),
    t('affiliate.form.phoneNumber.validation.valid')
  ),
  companyName: string().optional().nullable(),
})

const { handleSubmit } = useForm<Form>({
  validationSchema,
  initialValues: {
    phone: {
      code: '',
      number: '',
    },
  },
})

const firstName = useField<Form['firstName']>('firstName')
const lastName = useField<Form['lastName']>('lastName')
const email = useField<Form['email']>('email')
const phone = useField<Form['phone']>('phone')
const companyName = useField<Form['companyName']>('companyName')

const onSubmit = handleSubmit((values) => {
  emit('submit', values)
})
</script>
